import { Component, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import { PaginatedContent } from "../../../../api/common.types";
import { AccountFilter } from "../../home-admin-data/account.types";
import { map, Observable, shareReplay, Subject, take, takeUntil } from "rxjs";
import { BreakpointObserver } from "@angular/cdk/layout";
import { NzSelectOptionInterface } from "ng-zorro-antd/select";
import { UntypedFormBuilder } from "@angular/forms";
import { JobOffer, JobOpportunitySearchResultStatus } from "../../../../api/job-offer/job-offer.types";
import { AccountService } from "../../home-admin-data/account.service";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { TranslateService } from "@ngx-translate/core";
import { NzTableQueryParams } from "ng-zorro-antd/table";
import { AdminJobOfferEditComponent } from "./admin-job-offer-edit/admin-job-offer-edit.component";
import { AdminJobOfferCreateComponent } from "./admin-job-offer-create/admin-job-offer-create.component";
import { AdminJobOfferService } from "../../../../api/admin-job-offer/admin-job-offer.service";
import { ActivatedRoute } from "@angular/router";
import { Clipboard } from "@angular/cdk/clipboard";
import { MhNotificationType, MhSocketMessage } from "../../../../api/notification/notification.types";
import { SocketService } from "../../../../../../../core/src/lib/soket/socket.service";
import { updateJOListItem } from "../../../home-mission-partner/subpages/mission-partner-job-offers/job-offer-data/store/job-offer.reducer";
import { FilterStorageService } from "../../../../api/filter-storage/filter-storage.service";
import { FiltersPage, JobOfferListForm } from "../../../../api/filter-storage/filter-storage.types";
import { DatePipe } from "@angular/common";
import { CmsCachedService } from "libs/platform-pages/src/lib/api/cms/cms-cached.service";
import { MissionPartnerBasicInfo } from "../../home-admin-data/account.types";
import { JobOfferService } from "../../../../api/job-offer/job-offer.service";
import { APP_CONFIGURATION } from "../../../../../../../core/src/lib/core.di";
import { Inject } from "@angular/core";
import { AppConfig } from "../../../../../../../core/src/lib/app-config/config.types";
import { TableStateManager } from "../../utils/table-state-manager";
import { toTranslatedOptions } from "../../utils/translated-options";
import { PermissionsService } from "../../../../api/authorization/permissions.service";

const FILTERS_PAGE = FiltersPage.JOB_OFFER_LIST;
// keep in sync with the media query in admin-job-offer-list.component.less
const MOBILE_LAYOUT_QUERY = "(max-width: 1423px)";

@Component({
  selector: "mh-admin-job-offer-list",
  templateUrl: "./admin-job-offer-list.component.html",
  styleUrls: ["./admin-job-offer-list.component.less"],
})
export class AdminJobOfferListComponent implements OnInit, OnDestroy {
  tableState = new TableStateManager<JobOffer>((filter) => this.adminJobOfferService.loadJobOfferList(filter));
  
  missionPartnersWithActiveJobOffers: MissionPartnerBasicInfo[] = [];
  // [nzOptions] for the per-row selects, <nz-option *ngFor> would create rows x mission partners components
  missionPartnerOptions: NzSelectOptionInterface[] = [];
  stageOptions$: Observable<NzSelectOptionInterface[]>;
  isMobileLayout$: Observable<boolean>;
  canWrite = false;

  isApiLoading = false;
  searchForm!: JobOfferListForm;
  private queryMissionPartnerId: string | undefined;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private accountService: AccountService,
    private adminJobOfferService: AdminJobOfferService,
    private filterStorageService: FilterStorageService,
    private notificationService: NzNotificationService,
    private modal: NzModalService,
    private viewContainerRef: ViewContainerRef,
    private readonly translateService: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
    private clipboard: Clipboard,
    private socketService: SocketService,
    private datePipe: DatePipe,
    private cmsCachedService: CmsCachedService,
    private jobOfferService: JobOfferService,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfig,
    private breakpointObserver: BreakpointObserver,
    private permissionsService: PermissionsService,
  ) {
    // shared by the stage select of every row
    this.stageOptions$ = toTranslatedOptions(
      this.cmsCachedService.getUserDataByName("jo-stage"),
      this.translateService,
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    // render only one layout instead of hiding the other with CSS, both are heavy with 50 rows
    this.isMobileLayout$ = this.breakpointObserver.observe(MOBILE_LAYOUT_QUERY).pipe(
      map((state) => state.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    // checked once for the page instead of a mhHasPermission directive (and store subscription) per row button
    this.permissionsService
      .hasPermission("jo_list.write")
      .pipe(takeUntil(this.destroy$))
      .subscribe((canWrite) => (this.canWrite = canWrite));
    this.queryMissionPartnerId = this.activatedRoute.snapshot.queryParams["missionPartnerId"];
    this.fetchMissionPartnerList();
    this.socketService.messages$.pipe(takeUntil(this.destroy$)).subscribe((message: MhSocketMessage) => {
      if (
        message.action === MhNotificationType.TALENTS_SEARCH_STARTED &&
        message.jobOpportunityId &&
        this.tableState.data?.items
      ) {
        this.tableState.data.items = updateJOListItem(this.tableState.data?.items || [], {
          id: message.jobOpportunityId,
          status: JobOpportunitySearchResultStatus.PENDING,
        });
      }
      if (
        message.action === MhNotificationType.TALENTS_SEARCH_FINISHED &&
        message.jobOpportunityId &&
        this.tableState.data?.items
      ) {
        this.tableState.data.items = updateJOListItem(this.tableState.data?.items || [], {
          id: message.jobOpportunityId,
          status: JobOpportunitySearchResultStatus.DONE,
        });
      }
    });
  }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      searchTerm: [null],
      archived: false,
      missionPartnerId: [this.queryMissionPartnerId || null],
      forMissionPartnerId: [null],
      published: false,
    });

    this.initSavedFilters();
    this.tableState.fetchData({ ...this.getDefaultQueryParams(), ...this.getFilterFormValues() });
    this.fetchAllMissionPartners();
  }

  private initSavedFilters(): void {
    const storedFilters = this.filterStorageService.getStoredFilters()?.[FILTERS_PAGE] || ({} as JobOfferListForm);

    if (this.queryMissionPartnerId) {
      storedFilters.missionPartnerId = this.queryMissionPartnerId;
    }
    this.searchForm.patchValue(storedFilters);
    this.filterStorageService.saveFilters(this.searchForm, FILTERS_PAGE);
  }

  onPageChange(pageIndex: number): void {
    this.tableState.onPageChange(pageIndex, () => this.getFilterFormValues());
  }

  fetchMissionPartnerList() {
    this.accountService
      .getMissionPartnersWithActiveJobOffers()
      .pipe(take(1))
      .subscribe((partners) => {
        this.missionPartnersWithActiveJobOffers = partners;
      });
  }

  fetchAllMissionPartners() {
    this.accountService
      .getMissionPartners()
      .pipe(take(1))
      .subscribe((partners) => {
        this.missionPartnerOptions = partners.map((partner) => ({ label: partner.name, value: partner.id }));
      });
  }

  setJobOpportunityStage(jobOpportunityId: string, stage: number) {
    this.adminJobOfferService.setJobOpportunityStage(jobOpportunityId, stage).subscribe((success) => {
      if (success) {
        this.notificationService.success("", this.translateService.instant("admin.jo.stage.set.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setJobOpportunityForMissionPartner(jobOpportunityId: string, accountId: string) {
    this.adminJobOfferService.setJobOpportunityForMissionPartner(jobOpportunityId, accountId).subscribe((success) => {
      if (success) {
        this.notificationService.success("", this.translateService.instant("admin.jo.set-for-mp.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.tableState.onQueryParamsChange(params, () => this.getFilterFormValues());
  }

  submitFilterForm(): void {
    this.filterStorageService.saveFilters(this.searchForm, FILTERS_PAGE);
    this.tableState.onQueryChange({
      paging: {
        page: 1,
        itemsOnPage: this.tableState.data?.paging?.itemsOnPage || 0,
      },
      ...this.getFilterFormValues(),
    });
  }

  getFilterFormValues() {
    const searchField = this.getSearchField("searchTerm");
    const archivedField = this.getSearchField("archived");
    const missionPartnerId = this.getSearchField("missionPartnerId");
    const forMissionPartnerId = this.getSearchField("forMissionPartnerId");
    const publishedField = this.getSearchField("published");

    const filterFormValues: Partial<AccountFilter> = {
      searchTerm: searchField?.value || "",
      filters: [],
      groupFilters: [],
    };

    if (archivedField?.value) {
      filterFormValues.filters?.push({
        value: archivedField?.value,
        field: "ARCHIVED",
      });
    }

    if (publishedField?.value) {
      filterFormValues.filters?.push({
        value: publishedField?.value,
        field: "PUBLISHED",
      });
    }

    if (missionPartnerId?.value) {
      filterFormValues.filters?.push({
        value: missionPartnerId.value,
        field: "MISSION_PARTNER",
      });
    }

    if (forMissionPartnerId?.value) {
      filterFormValues.filters?.push({
        value: forMissionPartnerId.value,
        field: "FOR_MISSION_PARTNER",
      });
    }

    return filterFormValues;
  }

  getSearchField(name: string) {
    return this.searchForm.get(name);
  }

  getDefaultQueryParams() {
    const queryParams: AccountFilter = {
      paging: {
        page: 1,
        itemsOnPage: 50,
      },
      searchTerm: "",
      filters: [],
    };

    if (this.queryMissionPartnerId) {
      queryParams.filters?.push({
        value: this.queryMissionPartnerId,
        field: "MISSION_PARTNER",
      });
    }

    return queryParams;
  }

  openCreateJobOfferModal() {
    const modal = this.modal.create({
      nzContent: AdminJobOfferCreateComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzWidth: 800,
      nzMaskClosable: false,
    });

    modal.afterClose.subscribe((result: string) => {
      if (result === "success") {
        this.submitFilterForm();
      }
    });
  }

  editJobOffer(id: string): void {
    this.adminJobOfferService.getJobOpportunity(id).subscribe((jo) => this.openEditJobOfferModal(jo));
  }

  openEditJobOfferModal(jobOffer: JobOffer) {
    const modal = this.modal.create({
      nzContent: AdminJobOfferEditComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzWidth: 800,
      nzData: { jobOffer },
      nzMaskClosable: false,
    });

    modal.afterClose.subscribe((result: JobOffer) => {
      if (result && this.tableState.data?.items) {
        this.tableState.data.items = updateJOListItem(this.tableState.data.items, result);
      }
    });
  }

  showDeleteConfirm(jobOffer: JobOffer) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.jo.delete.confirm.title"),
      nzContent: `<b>${this.translateService.instant("admin.jo.delete.confirm.text")}</b>`,
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.deleteJobOffer(jobOffer.id),
    });
  }

  showArchiveConfirm(jobOffer: JobOffer) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.jo.archive.confirm.title"),
      nzContent: `<b>${this.translateService.instant("admin.jo.archive.confirm.text")}</b>`,
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.archiveJobOffer(jobOffer),
    });
  }

  archiveJobOffer(jobOffer: JobOffer) {
    this.adminJobOfferService.archiveJobOpportunity(jobOffer.id).subscribe((success) => {
      if (success) {
        jobOffer.archived = true;
        this.notificationService.success("", this.translateService.instant("admin.jo.archive.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  deleteJobOffer(jobOfferId: string) {
    this.adminJobOfferService.deleteJobOpportunity(jobOfferId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.filter((jo) => jo.id !== jobOfferId);
        }
        this.notificationService.success("", this.translateService.instant("admin.jo.delete.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  copyId(id: string) {
    const isCopySuccessful = this.clipboard.copy(id);
    if (isCopySuccessful) {
      this.notificationService.success("", this.translateService.instant("admin.copy-id.success"));
    }
  }

  onSignInAs(accountId: string) {
    this.accountService.signInAs(accountId);
  }

  downloadJobOfferListCSV() {
    const currentDate = new Date();
    const formattedDate = this.datePipe.transform(currentDate, "yyyy-MM-dd");
    const fileName = `${formattedDate}_job_offer_list_export.csv`;

    this.isApiLoading = true;
    this.adminJobOfferService.downloadJobOfferList(fileName).subscribe(() => (this.isApiLoading = false));
  }

  get isJobOpportunityPublishingEnabled(): boolean {
    return this.appConfiguration.featureStatus.jobOpportunityPublishing;
  }

  publishJobOpportunity(jobOpportunityId: string) {
    this.jobOfferService.adminPublishJobOpportunity(jobOpportunityId).subscribe({
      next: (success) => {
        if (success) {
          // Update the local data
          if (this.tableState.data?.items) {
            const jobOffer = this.tableState.data.items.find((jo) => jo.id === jobOpportunityId);
            if (jobOffer) {
              jobOffer.published = true;
            }
          }
          this.notificationService.success(
            this.translateService.instant("job-opportunity.publish.success.title"),
            this.translateService.instant("job-opportunity.publish.success.message"),
          );
        }
      },
      error: () => {
        this.notificationService.error(
          this.translateService.instant("job-opportunity.publish.error.title"),
          this.translateService.instant("job-opportunity.publish.error.message"),
        );
      },
    });
  }

  unpublishJobOpportunity(jobOpportunityId: string) {
    this.jobOfferService.adminUnpublishJobOpportunity(jobOpportunityId).subscribe({
      next: (success) => {
        if (success) {
          // Update the local data
          if (this.tableState.data?.items) {
            const jobOffer = this.tableState.data.items.find((jo) => jo.id === jobOpportunityId);
            if (jobOffer) {
              jobOffer.published = false;
            }
          }
          this.notificationService.success(
            this.translateService.instant("job-opportunity.unpublish.success.title"),
            this.translateService.instant("job-opportunity.unpublish.success.message"),
          );
        }
      },
      error: () => {
        this.notificationService.error(
          this.translateService.instant("job-opportunity.unpublish.error.title"),
          this.translateService.instant("job-opportunity.unpublish.error.message"),
        );
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  protected readonly JobOpportunitySearchResultStatus = JobOpportunitySearchResultStatus;
}
